"use client";

const DEFAULT_POSTHOG_KEY = "phc_nhQGTaFVRNDzUoLjdNAXBKBuiVre9zTbGV3XeZTb3adv";
const DEFAULT_POSTHOG_HOST = "https://us.i.posthog.com";
const DISABLED_VALUES = new Set(["", "0", "false", "off", "disabled"]);

type PostHogClient = typeof import("posthog-js").default;
type RoaveUser = Record<string, unknown> | null | undefined;

let posthogClientPromise: Promise<PostHogClient | null> | null = null;
let posthogInitialized = false;

const getEnvValue = (value: string | undefined, fallback: string) => {
  const normalized = value?.trim() ?? "";
  return normalized.length > 0 ? normalized : fallback;
};

const getPostHogConfig = () => {
  const key = getEnvValue(process.env.NEXT_PUBLIC_POSTHOG_KEY, DEFAULT_POSTHOG_KEY);
  const host = getEnvValue(process.env.NEXT_PUBLIC_POSTHOG_HOST, DEFAULT_POSTHOG_HOST).replace(/\/+$/, "");

  return {
    key,
    host,
    enabled: !DISABLED_VALUES.has(key.toLowerCase()),
  };
};

const isBrowser = () => typeof window !== "undefined";

export const initPostHog = async () => {
  if (!isBrowser()) return null;

  const config = getPostHogConfig();
  if (!config.enabled) return null;

  if (!posthogClientPromise) {
    posthogClientPromise = import("posthog-js")
      .then(({ default: posthog }) => {
        if (!posthogInitialized) {
          posthog.init(config.key, {
            api_host: config.host,
            capture_pageview: false,
            autocapture: true,
            capture_exceptions: true,
            disable_session_recording: false,
            persistence: "localStorage+cookie",
            session_recording: {
              maskAllInputs: true,
            },
          } as any);
          posthogInitialized = true;
        }

        return posthog;
      })
      .catch((error) => {
        console.warn("PostHog initialization failed", error);
        posthogClientPromise = null;
        posthogInitialized = false;
        return null;
      });
  }

  return posthogClientPromise;
};

const stringValue = (value: unknown) => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
};

export const getPostHogDistinctId = (user: RoaveUser) => {
  if (!user) return "";

  return (
    stringValue(user._id) ||
    stringValue(user.id) ||
    stringValue(user.user_id) ||
    stringValue(user.userId) ||
    stringValue(user.sub) ||
    stringValue(user.google_id) ||
    stringValue(user.email)
  );
};

const buildUserProperties = (user: RoaveUser, distinctId: string) => {
  if (!user) return {};

  const firstName = stringValue(user.first_name) || stringValue(user.given_name);
  const lastName = stringValue(user.last_name) || stringValue(user.family_name);
  const fullName =
    stringValue(user.name) ||
    [firstName, lastName].filter(Boolean).join(" ").trim() ||
    undefined;

  return {
    roave_user_id: distinctId,
    email: stringValue(user.email) || undefined,
    name: fullName,
    first_name: firstName || undefined,
    last_name: lastName || undefined,
    phone_number: stringValue(user.phone_number) || undefined,
    country_code: stringValue(user.country_code) || undefined,
    currency: stringValue(user.currency) || undefined,
    login_via: stringValue(user.login_via) || undefined,
    registration_via: stringValue(user.registration_via) || undefined,
    google_id: stringValue(user.google_id) || undefined,
    is_roave_customer: true,
  };
};

export const capturePostHogEvent = async (event: string, properties: Record<string, unknown> = {}) => {
  if (!event || !isBrowser()) return;

  const posthog = await initPostHog();
  posthog?.capture(event, properties);
};

export const capturePostHogPageView = async (properties: Record<string, unknown> = {}) => {
  if (!isBrowser()) return;

  await capturePostHogEvent("$pageview", {
    current_url: window.location.href,
    pathname: window.location.pathname,
    search: window.location.search,
    title: document.title,
    ...properties,
  });
};

export const identifyPostHogUser = async (user: RoaveUser) => {
  const distinctId = getPostHogDistinctId(user);
  if (!distinctId || !isBrowser()) return "";

  const posthog = await initPostHog();
  const userProperties = buildUserProperties(user, distinctId);

  posthog?.identify(distinctId, userProperties, {
    first_seen_source: "web",
  });
  posthog?.register({
    roave_user_id: distinctId,
    customer_email: stringValue(user?.email) || undefined,
    customer_country_code: stringValue(user?.country_code) || undefined,
    customer_currency: stringValue(user?.currency) || undefined,
  });

  return distinctId;
};

export const registerPostHogSuperProperties = async (properties: Record<string, unknown>) => {
  if (!isBrowser()) return;

  try {
    const posthog = await initPostHog();
    posthog?.register(properties);
  } catch (error) {
    console.warn("PostHog super property registration failed", error);
  }
};

export const resetPostHog = async () => {
  if (!isBrowser()) return;

  const posthog = await initPostHog();
  posthog?.unregister("roave_user_id");
  posthog?.unregister("customer_email");
  posthog?.unregister("customer_country_code");
  posthog?.unregister("customer_currency");
  posthog?.reset();
};
